import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../../api";
import { PlanBadge } from "../../../components/badge";
import { Button, ButtonRow, PrimaryButton } from "../../../components/button";
import { Card, CardContent, CardGrid, CardHeader } from "../../../components/card";
import { AreaChart, PieChart } from "../../../components/chart";
import { CommandBar } from "../../../components/command-bar";
import { DailyUsageWidget } from "../../../components/daily-usage-widget";
import { Dashboard, DashboardTitle } from "../../../components/dashboard";
import { MultiDataPickerControl } from "../../../components/data-picker";
import { DateTimeLabel } from "../../../components/datetime-label";
import { DestructiveButton } from "../../../components/destructive-button";
import { Dropdown, DropdownItem } from "../../../components/dropdown";
import { FormControl, Select, TextArea, TextInput } from "../../../components/form";
import { Icon } from "../../../components/icon";
import { Column, Content, Row, Section } from "../../../components/layout";
import { ModelBadge } from "../../../components/model-icon";
import { RelativeTimeRangePicker } from "../../../components/relative-time-range";
import { SpecializedControl } from "../../../components/specializedObject";
import { TabControl } from "../../../components/tabcontrol";
import { DefaultRow, Table, TableActionCell, TableBody, TableCell, TableHeader, TableHeaderActionCell, TableHeaderCell, TableRow } from "../../../components/table";
import { CopiableText, Heading, InlineCode, MutedText, Paragraph } from "../../../components/text";
import { Information, Warning } from "../../../components/warn";
import { Constants } from "../../../constants";
import { getSession } from "../../../session";
import { acceptDiff } from "../../../ui/accept-diff";
import { requestAiEdit } from "../../../ui/ai-edit";
import { formatCurrency, formatNumber, formatPreciseCurrency, formatTokenCount } from "../../../ui/format";
import { showFlyout } from "../../../ui/flyout";
import { runOperation } from "../../../ui/operation";
import { showErrorToast } from "../../../ui/toast";
import { throttledCall } from "../../../ui/throttle";
import { find } from "../../../util";

const NotificationDestinations = [
    { value: "all-users", text: "All users" },
    { value: "new-users", text: "New users (created in the past 7 days)" },
    { value: "never-added-credits", text: "Users who never added credits" },
    { value: "free", text: "Free users" },
    { value: "pro", text: "Pro users" },
    { value: "max", text: "Max users" },
    { value: "has-spent", text: "Users who have billed usage" }
];

export default function () {
    const accounts = el.state.create(null);
    const dailyTokenUsage = el.state.create([]);
    const analytics = el.state.create(null);
    const notificationTitle = TextInput("adminNotificationTitle", "Notification title", { maxlength: 200 });
    const notificationDescription = TextArea("adminNotificationDescription", "", {
        maxlength: 2000,
        placeholder: "Describe the update users should know about.",
        rows: 7
    });
    const notificationDestinations = MultiDataPickerControl({
        data: NotificationDestinations,
        value: ["all-users"]
    });
    const notificationForm = SpecializedControl({
        fields: [
            {
                name: "title",
                label: "Title",
                control: notificationTitle
            },
            {
                name: "description",
                label: "Description",
                control: notificationDescription
            },
            {
                name: "destinations",
                label: "Destinations",
                control: notificationDestinations
            }
        ]
    });

    async function loadAccounts(showOperation = false) {
        const operation = async () => {
            const params = new URLSearchParams();
            const query = find("adminAccountSearch")?.value?.trim();
            const viewMode = find("adminAccountViewMode")?.value || "default";
            if (query) {
                params.set("query", query);
            }
            params.set("viewMode", viewMode);

            const result = await api.getJson(`/api/v1/admin/accounts?${params.toString()}`);
            accounts.value = result.data;
        };

        return showOperation
            ? await runOperation("Loading admin accounts...", operation)
            : await operation();
    }

    async function loadAnalytics(showOperation = true) {
        const operation = async () => {
            const timeRange = find("adminAnalyticsTimeRange")?.value;
            const params = new URLSearchParams();
            if (timeRange?.start) {
                params.set("start", timeRange.start);
            }
            if (timeRange?.end) {
                params.set("end", timeRange.end);
            }

            const result = await api.getJson(`/api/v1/admin/analytics?${params.toString()}`);
            analytics.value = result.data;
        };

        return showOperation
            ? await runOperation("Loading global analytics...", operation)
            : await operation();
    }

    function getNotificationDraft() {
        const value = notificationForm.value;
        return {
            title: value.title.trim(),
            description: value.description.trim(),
            destinations: value.destinations
        };
    }

    runOperation("Loading administration...", async () => {
        const [, dailyTokenUsageResult] = await Promise.all([
            loadAccounts(),
            api.getJson("/api/v1/admin/daily-token-usage"),
            loadAnalytics(false)
        ]);

        dailyTokenUsage.value = dailyTokenUsageResult.data || [];
    });

    return Dashboard(
        DashboardTitle(
            "Administration",
            "Manage accounts, inspect global usage and send account notifications."
        ),
        Section(
            el.state.listening([dailyTokenUsage], () =>
                DailyUsageWidget(dailyTokenUsage.value || [])
            )
        ),
        Section(
            TabControl([
                {
                    title: "Accounts",
                    icon: Icon("group-line"),
                    contents: el.fragment(
                        Section(
                            CommandBar(
                                FormControl("Search",
                                    TextInput("adminAccountSearch", "Name, e-mail or account id", {
                                        onInput() {
                                            throttledCall(() => loadAccounts(), 300);
                                        }
                                    })
                                ),
                                FormControl("View mode",
                                    Select({
                                        id: "adminAccountViewMode",
                                        value: "default",
                                        items: [
                                            { value: "default", text: "Default" },
                                            { value: "all", text: "All" },
                                            { value: "created", text: "Created" },
                                            { value: "usage", text: "Usage" },
                                            { value: "balance", text: "Balance" },
                                            { value: "negative-balance", text: "Negative Balance" },
                                            { value: "negative-custom", text: "Negative Custom" },
                                            { value: "disabled", text: "Disabled accounts" }
                                        ],
                                        onChange() {
                                            loadAccounts();
                                        }
                                    })
                                ),
                                Button(Icon("refresh-line"), "Refresh", {
                                    onClick() {
                                        loadAccounts(true);
                                    }
                                }),
                                Button(Icon("terminal-line"), "Open terminal", {
                                    onClick() {
                                        window.open(
                                            window.env.apiEndpoint + "/api/v1/accounts/admin-terminal?api-key=" + encodeURIComponent(getSession().sessionKey),
                                            "_blank"
                                        );
                                    }
                                })
                            )
                        ),
                        Section(
                            Card(
                                { classList: ["bg-card"] },
                                CardContent(
                                    Table(
                                        { classList: ["hoverable"], pagination: { perPage: 50 } },
                                        TableHeader(
                                            TableHeaderCell("Id."),
                                            TableHeaderCell("Created at"),
                                            TableHeaderCell("Account"),
                                            TableHeaderCell("E-mail"),
                                            TableHeaderCell("Plan"),
                                            TableHeaderCell("Balance"),
                                            TableHeaderCell("Usage 24h"),
                                            TableHeaderCell("Volume 24h"),
                                            TableHeaderActionCell("Actions")
                                        ),
                                        el.state.listening([accounts], () =>
                                            TableBody(
                                                accounts.value?.items.map(account =>
                                                    TableRow(
                                                        TableCell(CopiableText(`#${account.id.substr(-4)}`, account.id)),
                                                        TableCell(DateTimeLabel(account.createdAt)),
                                                        TableCell(account.name),
                                                        TableCell(account.email),
                                                        TableCell(PlanBadge(account.plan)),
                                                        TableCell(Number(account.currentBalance) === 0 ? MutedText("-") : formatCurrency(account.currentBalance)),
                                                        TableCell(Number(account.usage24h) === 0 ? MutedText("-") : formatPreciseCurrency(account.usage24h)),
                                                        TableCell(Number(account.volume24h) === 0 ? MutedText("-") : `${formatTokenCount(account.volume24h)} tokens`),
                                                        TableActionCell(
                                                            ButtonRow(
                                                                Button(Icon("arrow-right-line"), "View", {
                                                                    onClick() {
                                                                        page(`/dashboard/admin/accounts/${account.id}`);
                                                                    }
                                                                })
                                                            )
                                                        )
                                                    )
                                                )?.nullWhenEmpty() || DefaultRow("No accounts found."),
                                                {
                                                    mount() {
                                                        this.closest("table").refreshPagination();
                                                    }
                                                }
                                            )
                                        )
                                    )
                                )
                            )
                        )
                    )
                },
                {
                    title: "Analytics",
                    icon: Icon("bar-chart-2-line"),
                    contents: el.fragment(
                        Section(
                            CommandBar(
                                FormControl("Date range",
                                    RelativeTimeRangePicker({
                                        id: "adminAnalyticsTimeRange",
                                        onInput() {
                                            loadAnalytics();
                                        }
                                    })
                                ),
                                Button(Icon("refresh-line"), "Refresh", {
                                    onClick() {
                                        loadAnalytics();
                                    }
                                })
                            )
                        ),
                        el.state.listening([analytics], () => analytics.value
                            ? Content(
                                Section(
                                    Heading("Global"),
                                    CardGrid(
                                        Card(
                                            CardHeader("Token volume"),
                                            CardContent(
                                                el("h2", {
                                                    style: { margin: 0, fontWeight: 400 },
                                                    title: formatNumber(analytics.value.tokenVolume)
                                                }, formatTokenCount(analytics.value.tokenVolume))
                                            )
                                        ),
                                        Card(
                                            CardHeader("Cost entries"),
                                            CardContent(
                                                el("h2", {
                                                    style: { margin: 0, fontWeight: 400 },
                                                    title: formatNumber(analytics.value.costEntryCount)
                                                }, formatNumber(analytics.value.costEntryCount))
                                            )
                                        ),
                                        Card(
                                            CardHeader("Total transacted"),
                                            CardContent(
                                                el("h2", {
                                                    style: { margin: 0, fontWeight: 400 },
                                                    title: analytics.value.totalCost
                                                }, formatCurrency(analytics.value.totalCost))
                                            )
                                        )
                                    )
                                ),
                                Section(
                                    TabControl([
                                        {
                                            title: "Usage per model",
                                            contents: Card(
                                                CardContent(
                                                    Section(
                                                        Row(
                                                            Column(
                                                                AreaChart({
                                                                    series: analytics.value.charts?.models?.series || [],
                                                                    xaxis: {
                                                                        type: "datetime",
                                                                        tickAmount: 4
                                                                    },
                                                                    yaxis: {
                                                                        show: false,
                                                                        labels: {
                                                                            show: false
                                                                        }
                                                                    }
                                                                })
                                                            ),
                                                            Column(
                                                                (() => {
                                                                    if (!analytics.value.models.length) return null;

                                                                    const top6 = analytics.value.models.slice(0, 6);
                                                                    const othersTotal = analytics.value.models.slice(6)
                                                                        .reduce((sum, model) => sum + Number(model.cost), 0);
                                                                    const labels = top6.map(model => model.name);
                                                                    const series = top6.map(model => Number(model.cost));

                                                                    if (othersTotal > 0) {
                                                                        labels.push("Others");
                                                                        series.push(othersTotal);
                                                                    }

                                                                    return PieChart({ series, labels, height: 300 });
                                                                })()
                                                            )
                                                        )
                                                    ),
                                                    Section(
                                                        Table(
                                                            { classList: ["hoverable"], pagination: { perPage: 20 } },
                                                            TableHeader(
                                                                TableHeaderCell("Model name"),
                                                                TableHeaderCell("Entries"),
                                                                TableHeaderCell("Quantity"),
                                                                TableHeaderCell("Total usage")
                                                            ),
                                                            TableBody(
                                                                analytics.value.models.map(model =>
                                                                    TableRow(
                                                                        TableCell(ModelBadge(model.name)),
                                                                        TableCell(formatNumber(model.entryCount)),
                                                                        TableCell(formatNumber(model.quantity), { title: model.quantity }),
                                                                        TableCell(formatPreciseCurrency(model.cost), { title: model.cost })
                                                                    )
                                                                )?.nullWhenEmpty() || DefaultRow("There's nothing here.")
                                                            )
                                                        )
                                                    )
                                                )
                                            )
                                        },
                                        {
                                            title: "Usage per SKU",
                                            contents: Card(
                                                CardContent(
                                                    Section(
                                                        AreaChart({
                                                            series: analytics.value.charts?.categories?.series || [],
                                                            xaxis: {
                                                                type: "datetime",
                                                                tickAmount: 4
                                                            },
                                                            yaxis: {
                                                                show: false,
                                                                labels: {
                                                                    show: false
                                                                }
                                                            }
                                                        })
                                                    ),
                                                    Section(
                                                        Table(
                                                            { classList: ["hoverable"], pagination: { perPage: 20 } },
                                                            TableHeader(
                                                                TableHeaderCell("SKU"),
                                                                TableHeaderCell("Entries"),
                                                                TableHeaderCell("Quantity"),
                                                                TableHeaderCell("Total usage")
                                                            ),
                                                            TableBody(
                                                                analytics.value.skus.map(sku =>
                                                                    TableRow(
                                                                        TableCell(InlineCode(sku.name)),
                                                                        TableCell(formatNumber(sku.entryCount)),
                                                                        TableCell(formatNumber(sku.quantity), { title: sku.quantity }),
                                                                        TableCell(formatPreciseCurrency(sku.cost), { title: sku.cost })
                                                                    )
                                                                )?.nullWhenEmpty() || DefaultRow("There's nothing here.")
                                                            )
                                                        )
                                                    )
                                                )
                                            )
                                        }
                                    ])
                                )
                            )
                            : el("div", MutedText("Loading analytics..."))
                        )
                    )
                },
                {
                    title: "Notifications",
                    icon: Icon("notification-3-line"),
                    contents: el.fragment(
                        Section(
                            Card(
                                { classList: ["bg-card"] },
                                CardHeader("Compose notification", "Prepare an in-dashboard notification for selected account audiences."),
                                CardContent(
                                    notificationForm,
                                    Section(
                                        CommandBar(
                                            ButtonRow(
                                                Dropdown("Use template",
                                                    DropdownItem("Privacy Policy update", Icon("shield-check-line"), {
                                                        onClick() {
                                                            notificationTitle.value = "Privacy Policy update";
                                                            notificationDescription.value = "We updated our Privacy Policy. Please review the latest version to understand how AIVAX collects, uses and protects your information.";
                                                        }
                                                    }),
                                                    DropdownItem("Terms of Use update", Icon("file-list-3-line"), {
                                                        onClick() {
                                                            notificationTitle.value = "Terms of Use update";
                                                            notificationDescription.value = "We updated our Terms of Use. Please review the latest version, which applies to your continued use of AIVAX services.";
                                                        }
                                                    }),
                                                    DropdownItem("Third-party providers update", Icon("route-line"), {
                                                        onClick() {
                                                            notificationTitle.value = "Third-party providers update";
                                                            notificationDescription.value = "We updated the list of third-party providers used to deliver AIVAX services. Please review the latest information in our legal documentation.";
                                                        }
                                                    })
                                                ),
                                                Button(Icon("quill-pen-ai-line"), "Edit with AI", {
                                                    onClick() {
                                                        const draft = getNotificationDraft();
                                                        requestAiEdit({
                                                            inputData: {
                                                                title: draft.title,
                                                                description: draft.description
                                                            },
                                                            subject: "administrative notification",
                                                            description: "Improve the title and description while preserving the notification's meaning.",
                                                            responseSchema: {
                                                                type: "object",
                                                                additionalProperties: false,
                                                                required: ["title", "description"],
                                                                properties: {
                                                                    title: { type: "string", description: "Concise notification title" },
                                                                    description: { type: "string", description: "Clear notification description" }
                                                                }
                                                            }
                                                        }).then(result =>
                                                            acceptDiff(
                                                                JSON.stringify({ title: draft.title, description: draft.description }, null, 2),
                                                                JSON.stringify(result, null, 2)
                                                            ).then(() => {
                                                                notificationTitle.value = result.title;
                                                                notificationDescription.value = result.description;
                                                            })
                                                        ).catch(() => { });
                                                    }
                                                })
                                            ),
                                            PrimaryButton(Icon("send-plane-line"), "Review and send", {
                                                onClick() {
                                                    const draft = getNotificationDraft();
                                                    if (!draft.title) {
                                                        showErrorToast("Enter a notification title.");
                                                        return;
                                                    }
                                                    if (!draft.description) {
                                                        showErrorToast("Enter a notification description.");
                                                        return;
                                                    }
                                                    if (!draft.destinations.length) {
                                                        showErrorToast("Select at least one destination.");
                                                        return;
                                                    }

                                                    runOperation("Reviewing notification...", async () => {
                                                        const review = await api.postJson("/v1/chat/completions", {
                                                            model: Constants.DefaultAiEditModelName,
                                                            messages: [
                                                                {
                                                                    role: "system",
                                                                    content: "Review an administrative user notification. Check whether it makes sense, contains spelling or grammar errors, is misleading, incomplete, unsafe, or has any issue serious enough to block sending. Give a very short opinion in the notification language. Do not rewrite the notification."
                                                                },
                                                                {
                                                                    role: "user",
                                                                    content: JSON.stringify({ title: draft.title, description: draft.description })
                                                                }
                                                            ],
                                                            json_only: true,
                                                            response_schema: {
                                                                type: "object",
                                                                additionalProperties: false,
                                                                required: ["opinion", "shouldBlock"],
                                                                properties: {
                                                                    opinion: { type: "string", description: "Very short review, no more than two sentences" },
                                                                    shouldBlock: { type: "boolean", description: "Whether the notification should be blocked from sending" }
                                                                }
                                                            }
                                                        });

                                                        if (!review || review.error) {
                                                            return;
                                                        }

                                                        const reviewFlyout = showFlyout(
                                                            { style: { width: "600px" } },
                                                            Section(
                                                                Paragraph("AI review"),
                                                                review.shouldBlock
                                                                    ? Warning(review.opinion)
                                                                    : Information(review.opinion)
                                                            ),
                                                            Section(
                                                                DestructiveButton(
                                                                    Icon("send-plane-line"),
                                                                    "Send notification",
                                                                    {
                                                                        delayAmount: 5,
                                                                        async onDestruct() {
                                                                            await runOperation("Sending notification...", async () => {
                                                                                const response = await api.postJson("/api/v1/admin/notifications", draft);
                                                                                if (!response?.error) {
                                                                                    reviewFlyout.dismiss();
                                                                                }
                                                                            });
                                                                        }
                                                                    }
                                                                )
                                                            )
                                                        );
                                                    });
                                                }
                                            })
                                        )
                                    )
                                )
                            )
                        )
                    )
                }
            ], {
                type: "fixed"
            })
        )
    );
}
